import React from "react";
import { ItemName, CheckboxItem } from "../../types";

interface CheckboxGroupProps {
  items: readonly string[];
  selectedItems: CheckboxItem[];
  maxSelections?: number;
  minSelections?: number;
  showYearsInput?: boolean;
  allowOther?: boolean;
  onSelectionChange: (items: CheckboxItem[]) => void;
  lockedItems?: string[];
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  items,
  selectedItems,
  maxSelections = 0,
  minSelections = 0,
  showYearsInput = false,
  allowOther = false,
  onSelectionChange,
  lockedItems = [],
}) => {
  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (checked) {
      if (maxSelections > 0 && selectedItems.length >= maxSelections) {
        return;
      }
      if (lockedItems.includes(name)) {
        return;
      }
      onSelectionChange([...selectedItems, { name: name as ItemName }]);
    } else {
      if (lockedItems.includes(name)) {
        return;
      }
      onSelectionChange(selectedItems.filter((item) => item.name !== name));
    }
  };

  const handleYearsChange = (name: string, years: string) => {
    onSelectionChange(
      selectedItems.map((item) =>
        item.name === name
          ? { ...item, yearsOfExperience: parseInt(years) || undefined }
          : item
      )
    );
  };

  const handleOtherValueChange = (checked: boolean, value: string = "") => {
    if (checked) {
      if (maxSelections > 0 && selectedItems.length >= maxSelections) {
        return;
      }
      onSelectionChange([
        ...selectedItems,
        { name: "Other" as ItemName, isOther: true, otherValue: value },
      ]);
    } else {
      onSelectionChange(selectedItems.filter((item) => !item.isOther));
    }
  };

  const handleOtherInputChange = (value: string) => {
    onSelectionChange(
      selectedItems.map((item) =>
        item.isOther ? { ...item, otherValue: value } : item
      )
    );
  };

  return (
    <div className="checkbox-group">
      {items.map((name) => {
        const isSelected = selectedItems.some((item) => item.name === name);
        const item = selectedItems.find((item) => item.name === name);
        const isLocked = lockedItems.includes(name);

        return (
          <div
            key={name}
            className={`checkbox-item ${isSelected ? "selected" : ""} ${
              isLocked ? "locked" : ""
            }`}
            style={{ opacity: isLocked ? 0.7 : 1 }}
          >
            <div className="checkbox-row">
              <label className={`checkbox-label ${isLocked ? "locked" : ""}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleCheckboxChange(name, e.target.checked)}
                  disabled={
                    !isSelected &&
                    maxSelections > 0 &&
                    selectedItems.length >= maxSelections
                  }
                />
                {name}
              </label>
              {showYearsInput && isSelected && (
                <div className="years-input-inline">
                  <input
                    type="number"
                    min="0"
                    placeholder="Years of clinical experience"
                    value={item?.yearsOfExperience || ""}
                    onChange={(e) => handleYearsChange(name, e.target.value)}
                    required
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {allowOther && (
        <div
          className={`checkbox-item ${
            selectedItems.some((item) => item.isOther) ? "selected" : ""
          }`}
        >
          <div className="checkbox-row">
            <label className="checkbox-label other-label">
              <input
                type="checkbox"
                checked={selectedItems.some((item) => item.isOther)}
                onChange={(e) => handleOtherValueChange(e.target.checked)}
                disabled={
                  !selectedItems.some((item) => item.isOther) &&
                  maxSelections > 0 &&
                  selectedItems.length >= maxSelections
                }
              />
              Other
            </label>
            {selectedItems.some((item) => item.isOther) && (
              <input
                type="text"
                className="other-input"
                value={
                  selectedItems.find((item) => item.isOther)?.otherValue || ""
                }
                onChange={(e) => handleOtherInputChange(e.target.value)}
                placeholder="Please specify"
                required
              />
            )}
            {showYearsInput && selectedItems.some((item) => item.isOther) && (
              <div className="years-input-inline">
                <input
                  type="number"
                  min="0"
                  placeholder="Years of clinical experience"
                  value={
                    selectedItems.find((item) => item.isOther)
                      ?.yearsOfExperience || ""
                  }
                  onChange={(e) => handleYearsChange("Other", e.target.value)}
                  required
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckboxGroup;
